import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  FileDown,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Info,
  ExternalLink,
} from "lucide-react";

const VENDORS = [
  {
    id: "sandata",
    label: "Sandata",
    blurb: "States: CT, FL, IN, NC, NM, RI, SC, TN, VA, MO, OK, NH, …",
    portalUrl: "https://www.sandata.com/",
  },
  {
    id: "hhaexchange",
    label: "HHAeXchange",
    blurb: "States: NY (most MCOs), NJ, IL, MA, MO, MN, …",
    portalUrl: "https://www.hhaexchange.com/",
  },
];

// Default to "this month" — most agencies submit weekly or monthly.
function defaultRange() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(first), to: fmt(today) };
}

export default function AdminEvvExport() {
  const [{ from, to }, setRange] = useState(defaultRange);
  const [vendor, setVendor] = useState("sandata");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [includeBlocked, setIncludeBlocked] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!from || !to) return;
    setLoadingPreview(true);
    try {
      const { data } = await api.get("/admin/evv/preview", {
        params: { from_date: from, to_date: to, vendor },
      });
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load preview");
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }, [from, to, vendor]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const download = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/admin/evv/export.csv", {
        params: {
          from_date: from,
          to_date: to,
          vendor,
          include_blocked: includeBlocked,
        },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `afyanex-evv-${vendor}-${from}-to-${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const count = resp.headers["x-record-count"] || "?";
      toast.success(`Downloaded ${count} visit${count === "1" ? "" : "s"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="evv-export-page">
      <header>
        <p className="label-eyebrow">Compliance · 21st Century Cures Act</p>
        <h1 className="font-heading text-3xl font-medium tracking-tight">
          EVV Export
        </h1>
        <p className="text-sm text-clinical-subtext mt-1 max-w-2xl">
          Generate visit verification files for Sandata or HHAeXchange. Upload the
          resulting CSV to your vendor's portal — auto-submission via SFTP/API
          coming in a future release.
        </p>
      </header>

      {/* Vendor pick */}
      <div className="card-surface p-5 space-y-4">
        <div className="flex items-center gap-2">
          <FileDown className="h-4 w-4 text-clinical-primary" />
          <h2 className="font-heading text-lg font-medium">1 · Choose vendor</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {VENDORS.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setVendor(v.id)}
              className={`text-left rounded-xl border-2 p-4 transition ${
                vendor === v.id
                  ? "border-clinical-primary bg-clinical-primary/5"
                  : "border-clinical-border bg-white hover:border-clinical-primary/40"
              }`}
              data-testid={`evv-vendor-${v.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-medium text-clinical-text">{v.label}</div>
                {vendor === v.id && (
                  <CheckCircle2 className="h-4 w-4 text-clinical-primary" />
                )}
              </div>
              <p className="text-xs text-clinical-subtext mt-1">{v.blurb}</p>
              <a
                href={v.portalUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[11px] text-clinical-primary hover:underline mt-2"
              >
                Vendor portal <ExternalLink className="h-3 w-3" />
              </a>
            </button>
          ))}
        </div>
      </div>

      {/* Date range */}
      <div className="card-surface p-5 space-y-4">
        <h2 className="font-heading text-lg font-medium">2 · Pick date range</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="label-eyebrow mb-1.5 block">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              className="input-clinical"
              data-testid="evv-from-input"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              className="input-clinical"
              data-testid="evv-to-input"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={loadPreview}
              disabled={loadingPreview}
              className="btn-secondary w-full"
              data-testid="evv-refresh-preview"
            >
              {loadingPreview ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Refresh preview"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="card-surface p-5 space-y-4">
        <h2 className="font-heading text-lg font-medium">3 · Pre-flight check</h2>
        {loadingPreview && !preview ? (
          <div className="text-sm text-clinical-subtext">Loading preview…</div>
        ) : !preview ? (
          <div className="text-sm text-clinical-subtext">
            Set a date range to see how many visits are ready to submit.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Visits in range" value={preview.total_visits} testid="evv-stat-total" />
              <Stat
                label="Ready to submit"
                value={preview.ready_to_submit}
                tone={preview.ready_to_submit > 0 ? "good" : "neutral"}
                testid="evv-stat-ready"
              />
              <Stat
                label="Blocked"
                value={preview.blocked}
                tone={preview.blocked > 0 ? "warn" : "neutral"}
                testid="evv-stat-blocked"
              />
              <Stat
                label="Clients"
                value={Object.keys(preview.by_client || {}).length}
                testid="evv-stat-clients"
              />
            </div>

            {preview.blocked > 0 && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                data-testid="evv-blockers-panel"
              >
                <div className="flex items-center gap-1.5 font-medium mb-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Missing identifiers in {preview.blocked} visit
                  {preview.blocked === 1 ? "" : "s"}
                </div>
                <ul className="space-y-0.5 ml-5 list-disc">
                  {Object.entries(preview.blockers || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([field, count]) => (
                      <li key={field}>
                        <code className="text-[11px]">{field}</code>
                        <span className="text-amber-700"> · {count}</span>
                      </li>
                    ))}
                </ul>
                <p className="mt-2 text-amber-800">
                  Fix these in{" "}
                  <a href="/admin/settings" className="underline">
                    Settings
                  </a>{" "}
                  /{" "}
                  <a href="/admin/clients" className="underline">
                    Clients
                  </a>{" "}
                  /{" "}
                  <a href="/admin/team/caregivers" className="underline">
                    Team
                  </a>{" "}
                  — the vendor will reject these rows.
                </p>
              </div>
            )}

            {!preview.agency_ready && (
              <div
                className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900"
                data-testid="evv-agency-not-ready"
              >
                <div className="flex items-center gap-1.5 font-medium">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Agency Medicaid Provider ID is not set
                </div>
                <p className="mt-1">
                  Open <a href="/admin/settings" className="underline">Settings → Medicaid / EVV billing identifiers</a> to add it before exporting.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Download */}
      <div className="card-surface p-5 space-y-4">
        <h2 className="font-heading text-lg font-medium">4 · Download</h2>
        <label className="inline-flex items-start gap-2 text-xs text-clinical-subtext">
          <input
            type="checkbox"
            checked={includeBlocked}
            onChange={(e) => setIncludeBlocked(e.target.checked)}
            className="mt-0.5"
            data-testid="evv-include-blocked-toggle"
          />
          <span>
            Include blocked rows (rows with missing IDs).{" "}
            <span className="text-clinical-text">
              Useful for QA — your vendor will reject these on submission.
            </span>
          </span>
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={download}
            disabled={downloading || !preview || preview.total_visits === 0}
            className="btn-primary"
            data-testid="evv-download-button"
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <FileDown className="h-4 w-4" /> Download {vendor} CSV
              </>
            )}
          </button>
          {preview && preview.total_visits === 0 && (
            <span className="text-xs text-clinical-subtext">
              No visits in this range.
            </span>
          )}
        </div>
        <div className="flex items-start gap-2 text-[11px] text-clinical-subtext bg-clinical-bg/60 border border-clinical-border rounded-lg p-3">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Exact column requirements vary by state. We emit the most commonly
            required columns. <strong>Verify the file against your state's
            spec</strong> before your first production submission.
          </span>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "neutral", testid }) {
  const toneClass =
    tone === "good"
      ? "text-emerald-700"
      : tone === "warn"
      ? "text-amber-700"
      : "text-clinical-text";
  return (
    <div className="rounded-lg border border-clinical-border bg-white p-3" data-testid={testid}>
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className={`mt-0.5 text-2xl font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}
